import { useEffect, useMemo, useState } from "react";
import JSZip from "jszip";
import { Check, Download, FileArchive, PackageCheck, Sheet } from "lucide-react";
import { toast } from "sonner";
import { PRODUCT_DOWNLOADS } from "@/constants/site";
import { useTranslation } from "react-i18next";
import generatedTranslations from "@/constants/generatedTranslations.json";
import { downloadPlatformExport, PLATFORM_EXPORTS } from "@/lib/ecommerceExport";

const buildProductInfo = (brand, product, t) => `${t("media.infoBrand")}\n${brand.name}\n\n${t("media.infoProduct")}\n${product.name}\n\n${t("media.infoDescription")}\n${product.description}\n\n${t("media.infoBarcode")}\n${product.barcode}\n\n${t("media.infoSupplier")}\n${product.supplierCode}\n`;
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const assetUrl = (path) => path?.startsWith("/uploads/") ? `${API_ORIGIN}${path}` : path;
const CATEGORY_KEYS = { "Kedi Kumu": "catLitter", "Kedi Maması": "catFood", "Kedi Pouch": "catPouch", "Kedi Et Ezmesi": "catPaste", "köpek beslenmesi kategorisi": "dogNutrition", "profesyonel pet bakım kategorisi": "petCare", "tamamlayıcı beslenme kategorisi": "supplements" };
const localizedFallback = (t, language) => PRODUCT_DOWNLOADS.map((brand) => ({ ...brand, products: brand.products.map((product) => {
  const translatedProduct = generatedTranslations.products?.[language]?.find((item) => item.id === brand.id)?.products?.find((item) => item.id === product.id) || {};
  const subcategory = t(`productCategories.${CATEGORY_KEYS[product.subcategory] || "catFood"}`, { defaultValue: product.subcategory });
  return { ...product, ...translatedProduct, subcategory: translatedProduct.subcategory || subcategory, description: translatedProduct.description || (language === "tr" ? product.description : `${product.name} — ${subcategory}.`) };
}) }));

export const ProductDownloads = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "tr";
  const fallbackBrands = useMemo(() => localizedFallback(t, language), [t, language]);
  const [brands, setBrands] = useState(fallbackBrands);
  const [brandId, setBrandId] = useState(PRODUCT_DOWNLOADS[0].id);
  const [subcategory, setSubcategory] = useState("all");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isPreparing, setIsPreparing] = useState(false);
  const [platformId, setPlatformId] = useState("tsoft");
  const [isExporting, setIsExporting] = useState(false);
  useEffect(() => {
    let isMounted = true;
    setBrands(fallbackBrands);
    setBrandId(fallbackBrands[0].id);
    setSubcategory("all");
    setSelectedProductIds([]);
    fetch(`${API_ORIGIN}/api/media/products?language=${encodeURIComponent(language)}`)
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((data) => {
        if (isMounted && data.brands?.length) {
          setBrands(data.brands);
          setBrandId(data.brands[0].id);
          setSelectedProductIds([]);
        }
      })
      .catch(() => undefined);
    return () => { isMounted = false; };
  }, [fallbackBrands, language]);

  const selectedBrand = useMemo(() => brands.find((brand) => brand.id === brandId) ?? brands[0], [brandId, brands]);
  const allProducts = useMemo(() => brands.flatMap((brand) => brand.products.map((product) => ({ brand, product }))), [brands]);
  const selectedProducts = allProducts.filter(({ product }) => selectedProductIds.includes(product.id));
  const subcategories = useMemo(() => [...new Set(selectedBrand.products.map((product) => product.subcategory))], [selectedBrand]);
  const visibleProducts = subcategory === "all" ? selectedBrand.products : selectedBrand.products.filter((product) => product.subcategory === subcategory);
  const allVisibleSelected = visibleProducts.length > 0 && visibleProducts.every((product) => selectedProductIds.includes(product.id));

  const toggleProduct = (productId) => {
    setSelectedProductIds((current) => current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId]);
  };

  const toggleVisibleProducts = () => {
    const visibleIds = visibleProducts.map((product) => product.id);
    setSelectedProductIds((current) => allVisibleSelected ? current.filter((id) => !visibleIds.includes(id)) : [...new Set([...current, ...visibleIds])]);
  };

  const downloadProducts = async () => {
    if (!selectedProducts.length) {
      toast.info(t("media.chooseOne"));
      return;
    }

    setIsPreparing(true);
    try {
      const zip = new JSZip();
      const blobCache = new Map();
      const getBlob = async (path) => {
        if (!blobCache.has(path)) {
          const response = await fetch(assetUrl(path));
          if (!response.ok) throw new Error(path);
          blobCache.set(path, await response.blob());
        }
        return blobCache.get(path);
      };

      await Promise.all(selectedProducts.map(async ({ brand, product }) => {
        const folder = `${brand.id}/${product.id}`;
        zip.file(`${folder}/urun-bilgileri.txt`, buildProductInfo(brand, product, t));
        await Promise.all(product.files.map(async ({ path, filename }) => {
          zip.file(`${folder}/${filename}`, await getBlob(path));
        }));
      }));

      const archive = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(archive);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nolena-secili-urunler-${selectedProducts.length}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t("media.ready", { count: selectedProducts.length }));
    } catch {
      toast.error(t("media.error"));
    } finally {
      setIsPreparing(false);
    }
  };

  const downloadPlatformFile = async () => {
    if (!selectedProducts.length) {
      toast.info(t("media.chooseOne"));
      return;
    }
    setIsExporting(true);
    try {
      await downloadPlatformExport(platformId, selectedProducts, window.location.origin);
      toast.success(t("media.exportReady", { count: selectedProducts.length }));
    } catch {
      toast.error(t("media.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section data-testid="product-downloads" className="bg-cream px-5 py-12 sm:px-8 sm:py-16">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-3 text-sm text-ink/60"><FileArchive className="h-4 w-4 text-brand" /> {t("media.zipHint")}</div>
          <div className="mt-5 rounded-3xl border border-detail bg-surface p-5 shadow-[0_20px_70px_-45px_rgba(43,33,24,0.45)] sm:p-8">
          <div className="flex flex-col gap-4 border-b border-detail pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid w-full gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-ink">{t("media.brand")}
              <select value={brandId} onChange={(event) => { setBrandId(event.target.value); setSubcategory("all"); }} className="mt-2 w-full rounded-xl border border-detail bg-cream px-4 py-3 font-normal text-ink outline-none focus:border-brand">
                {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name} · {t("media.productsCount", { count: brand.products.length })}</option>)}
              </select>
            </label>
              <label className="text-sm font-semibold text-ink">{t("media.subcategory")}
                <select value={subcategory} onChange={(event) => setSubcategory(event.target.value)} className="mt-2 w-full rounded-xl border border-detail bg-cream px-4 py-3 font-normal text-ink outline-none focus:border-brand">
                  <option value="all">{t("media.allSubcategories")}</option>
                  {subcategories.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
            </div>
            <button type="button" onClick={toggleVisibleProducts} className="inline-flex items-center gap-2 text-sm font-semibold text-brand transition hover:text-brand2">
              <span className={`flex h-5 w-5 items-center justify-center rounded border ${allVisibleSelected ? "border-brand bg-brand text-cream" : "border-detail bg-surface"}`}>{allVisibleSelected && <Check className="h-3.5 w-3.5" />}</span>
              {allVisibleSelected ? t("media.clearVisible") : t("media.selectAll")}
            </button>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {visibleProducts.map((product) => {
              const isSelected = selectedProductIds.includes(product.id);
              return <label key={product.id} className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-3 transition ${isSelected ? "border-brand bg-brand/5" : "border-detail bg-cream/40 hover:border-brand/50"}`}>
                <input type="checkbox" checked={isSelected} onChange={() => toggleProduct(product.id)} className="sr-only" />
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${isSelected ? "border-brand bg-brand text-cream" : "border-detail bg-surface"}`}>{isSelected && <Check className="h-3.5 w-3.5" />}</span>
                <img src={assetUrl(product.image)} alt="" className="h-14 w-12 rounded-lg object-cover" />
                <span className="min-w-0"><span className="block truncate text-sm font-semibold text-ink">{product.name}</span><span className="mt-1 block text-xs text-ink/55">{product.subcategory} · {product.isSample ? t("media.sample") : t("media.code", { code: product.supplierCode })}</span></span>
              </label>;
            })}
          </div>

          <div className="mt-7 flex flex-col gap-4 rounded-2xl bg-ink px-5 py-4 text-cream sm:flex-row sm:items-center sm:justify-between">
            <div><span className="text-sm font-semibold">{t("media.selected", { count: selectedProducts.length })}</span><span className="mt-1 block text-xs text-cream/60">{t("media.mixBrands")}</span></div>
            <button type="button" onClick={downloadProducts} disabled={isPreparing || !selectedProducts.length} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-cream transition hover:bg-brand2 disabled:cursor-not-allowed disabled:opacity-50">
              {isPreparing ? <PackageCheck className="h-4 w-4 animate-pulse" /> : <Download className="h-4 w-4" />}
              {isPreparing ? t("media.preparing") : t("media.download")}
            </button>
            </div>
          </div>
          <section data-testid="platform-export" className="mt-5 rounded-3xl border border-brand/20 bg-brand/[.045] p-5 sm:p-6">
            <div className="flex items-start gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-brand text-cream"><Sheet className="h-5 w-5" /></span><div><h2 className="font-heading text-xl font-bold text-ink">{t("media.exportSection")}</h2><p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60">{t("media.exportHint")}</p></div></div>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"><label className="w-full max-w-md text-sm font-semibold text-ink">{t("media.exportPlatform")}<select value={platformId} onChange={(event) => setPlatformId(event.target.value)} className="mt-2 w-full rounded-xl border border-detail bg-surface px-4 py-3 font-normal text-ink outline-none focus:border-brand">{PLATFORM_EXPORTS.map((platform) => <option key={platform.id} value={platform.id}>{platform.label} · {platform.extension.toUpperCase()}</option>)}</select></label><button type="button" onClick={downloadPlatformFile} disabled={isExporting || !selectedProducts.length} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream transition hover:bg-brand disabled:cursor-not-allowed disabled:opacity-50">{isExporting ? <PackageCheck className="h-4 w-4 animate-pulse" /> : <Download className="h-4 w-4" />}{isExporting ? t("media.exportPreparing") : t("media.exportDownload")}</button></div>
          </section>
        </div>
    </section>
  );
};
